import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface QueryOptions {
  enabled?: boolean;
  refetchInterval?: number;
  staleTime?: number;
  cacheTime?: number;
}

interface QueryCache {
  data: any;
  timestamp: number;
  key: string;
}

// Simple in-memory cache
const queryCache = new Map<string, QueryCache>();

export function useOptimizedQuery<T = any>(
  key: string | string[],
  queryFn: () => Promise<{ data: T | null; error: any }>,
  options: QueryOptions = {}
) {
  const {
    enabled = true,
    refetchInterval,
    staleTime = 5 * 60 * 1000, // 5 minutes default
    cacheTime = 10 * 60 * 1000 // 10 minutes default
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<any>(null);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const cacheKey = Array.isArray(key) ? key.join('-') : key;

  const fetchData = useCallback(async (skipCache = false) => {
    if (!enabled) return;

    // Check cache first
    if (!skipCache) {
      const cached = queryCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < staleTime) {
        setData(cached.data);
        setLoading(false);
        return;
      }
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const result = await queryFn();
      
      if (result.error) {
        setError(result.error);
      } else {
        setData(result.data);
        // Cache the result
        queryCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now(),
          key: cacheKey
        });
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [cacheKey, enabled, queryFn, staleTime]);

  const refetch = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }

    if (refetchInterval && enabled) {
      intervalRef.current = setInterval(() => {
        fetchData(true);
      }, refetchInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, fetchData, refetchInterval]);

  // Cleanup old cache entries
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      for (const [key, cache] of queryCache.entries()) {
        if (now - cache.timestamp > cacheTime) {
          queryCache.delete(key);
        }
      }
    };

    const cleanupInterval = setInterval(cleanup, 60000); // Cleanup every minute
    return () => clearInterval(cleanupInterval);
  }, [cacheTime]);

  return {
    data,
    loading,
    error,
    refetch,
    isStale: () => {
      const cached = queryCache.get(cacheKey);
      return !cached || (Date.now() - cached.timestamp) > staleTime;
    }
  };
}

// Utility to invalidate cache
export function invalidateQuery(key: string | string[]) {
  const cacheKey = Array.isArray(key) ? key.join('-') : key;
  queryCache.delete(cacheKey);
}

// Utility to clear all cache
export function clearQueryCache() {
  queryCache.clear();
}